import { useState } from "react";

import { Alert, Button, Input, Space, Tag, Typography } from "antd";

import { diffPorPalabra } from "../../lib/suggestions/diff";
import type { Sugerencia } from "../../lib/suggestions/types";

const { TextArea } = Input;
const { Text } = Typography;

/**
 * Antes y después de una sugerencia, con tres acciones y ninguna más.
 *
 * "Editar" es la que más información deja: quien edita la sugerencia está
 * diciendo que la dirección era correcta pero la ejecución no, y eso no se
 * puede deducir de un aceptar ni de un rechazar.
 */

const Diff = ({ original, sugerido }: { original: string; sugerido: string }) => (
  <p className="cmp-diff">
    {diffPorPalabra(original, sugerido).map((t, i) => (
      <span key={i} className={`cmp-${t.tipo}`}>
        {t.texto}
      </span>
    ))}
  </p>
);

export const Comparador = ({
  sugerencia,
  onAceptar,
  onRechazar,
  onEditar,
}: {
  sugerencia: Sugerencia;
  onAceptar: () => void;
  onRechazar: () => void;
  onEditar: (texto: string) => void;
}) => {
  const [editando, setEditando] = useState(false);
  const [borrador, setBorrador] = useState(sugerencia.sugerido);

  const resuelta = sugerencia.estado !== "pendiente";

  return (
    <div className="cmp">
      <div className="cmp-cabecera">
        <Text strong>{sugerencia.etiqueta}</Text>
        {sugerencia.severidad === "alta" && <Tag color="red">Importante</Tag>}
        {resuelta && (
          <Tag color={sugerencia.estado === "rechazada" ? "default" : "green"}>
            {sugerencia.estado === "aceptada"
              ? "Aplicada"
              : sugerencia.estado === "editada"
                ? "Editada"
                : "Descartada"}
          </Tag>
        )}
      </div>

      <p className="cmp-razon">{sugerencia.razon}</p>

      {/* Un aviso no tiene "después" que enseñar: solo señala que falta algo
          que únicamente la persona puede poner. */}
      {sugerencia.aviso ? (
        <Alert
          type="info"
          showIcon
          message="Esto lo tienes que escribir tú"
          description="No podemos proponerte un texto sin inventarnos datos. Haz clic en la sección del documento para completarla."
        />
      ) : editando ? (
        <>
          <TextArea
            value={borrador}
            onChange={(e) => setBorrador(e.target.value)}
            autoSize={{ minRows: 5, maxRows: 18 }}
            autoFocus
          />
          <Space style={{ marginTop: 10 }}>
            <Button
              type="primary"
              onClick={() => {
                onEditar(borrador);
                setEditando(false);
              }}
            >
              Guardar mi versión
            </Button>
            <Button onClick={() => setEditando(false)}>Cancelar</Button>
          </Space>
        </>
      ) : (
        <>
          <Diff original={sugerencia.original} sugerido={sugerencia.sugerido} />

          {!resuelta && (
            <Space wrap style={{ marginTop: 12 }}>
              <Button type="primary" onClick={onAceptar}>
                Usar la sugerencia
              </Button>
              <Button onClick={onRechazar}>Dejar el mío</Button>
              <Button
                type="text"
                onClick={() => {
                  setBorrador(sugerencia.sugerido);
                  setEditando(true);
                }}
              >
                Editar
              </Button>
            </Space>
          )}
        </>
      )}
    </div>
  );
};
